import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Fade,
  Grow,
  Paper,
  Typography,
  useTheme,
  IconButton,
} from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { debounce } from "lodash";

/**
 * A generic virtualized list component that handles:
 * - Prepend/append items without scroll position jumps
 * - Infinite scroll in both directions
 * - Pull to refresh
 * - Dynamic item heights
 * - Automatic read detection
 *
 * @param {Object} props
 * @param {Array} props.items - The list items to render
 * @param {Function} props.renderItem - Function to render each item
 * @param {Function} props.loadMore - Function to load more items when scrolling
 * @param {Function} props.loadPrevious - Function to load previous items when scrolling up
 * @param {Function} props.onRefresh - Function to call when pull-to-refresh is triggered
 * @param {boolean} props.hasMore - Whether there are more items to load
 * @param {boolean} props.hasPrevious - Whether there are previous items to load
 * @param {boolean} props.isLoading - Whether the list is currently loading
 * @param {number} props.overscan - Number of items to render outside the visible area
 * @param {string} props.className - Additional CSS class name
 * @param {Object} props.style - Additional styles
 * @param {number} props.itemHeight - Height of each item
 * @param {string} props.containerHeight - Height of the container
 * @param {boolean} props.scrollTopTransition - Whether to animate scroll position changes
 * @param {string} props.newItemsText - Text to show for new items (default: "new items above")
 * @param {Function} props.isItemUnread - Function to determine if an item is unread
 * @param {Function} props.onAppend - Function to call when new items are appended
 * @param {Function} props.onPrepend - Function to call when new items are prepended
 * @param {Function} props.onRead - Function to call when an item is read, receives the item's id and filter
 * @param {React.Component} props.skeletonComponent - Component to render for loading states
 * @param {Object} props.emptyState - Configuration for empty state
 * @param {React.Component} props.emptyState.icon - Icon to show in empty state
 * @param {string} props.emptyState.title - Title text for empty state
 * @param {string} props.emptyState.description - Description text for empty state
 * @param {number} props.gap - Gap size between items in pixels (default: 8)
 */
const VirtualList = ({
  items,
  renderItem,
  loadMore,
  loadPrevious,
  hasMore = false,
  hasPrevious = false,
  isLoading = false,
  overscan = 5,
  className,
  style,
  itemHeight = 100,
  containerHeight = "100%",
  scrollTopTransition = true,
  newItemsText = "new items above",
  isItemUnread = () => false,
  onAppend = () => {},
  onPrepend = () => {},
  onRead = () => {},
  skeletonComponent: SkeletonComponent,
  emptyState,
  gap = 8,
}) => {
  const theme = useTheme();
  const parentRef = useRef(null);
  const lastScrollTop = useRef(0);
  const [actualItemsLength, setActualItemsLength] = useState(items.length);
  const [newItems, setNewItems] = useState(new Set());
  const [newItemsCount, setNewItemsCount] = useState(0);
  const isNearBottom = useRef(false);
  const [loadingHeight, setLoadingHeight] = useState(0);
  const [lastItemsLength, setLastItemsLength] = useState(items.length);
  const [lastFirstItem, setLastFirstItem] = useState(items[0]?.id);
  const [isUpdatingScroll, setIsUpdatingScroll] = useState(false);
  const lastUpdateTime = useRef(0);
  const lastItemsRef = useRef(items);
  const loadMoreTimeout = useRef(null);
  const lastReadId = useRef(null);
  const isFocused = useRef(true);
  const [showNewItems, setShowNewItems] = useState(false);
  const lastUnreadIndex = useRef(-1);
  const visibleItems = useRef(new Set());
  const initialLoadDone = useRef(false);
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Create a debounced version of onRead for new items
  const debouncedOnRead = useMemo(
    () =>
      debounce((id, filter) => {
        if (id !== lastReadId.current) {
          lastReadId.current = id;
          onRead(id, filter);
        }
      }, 1000),
    [onRead]
  );

  // Function to find first unread item index
  const findFirstUnreadIndex = useCallback(() => {
    for (let i = 0; i < items.length; i++) {
      if (isItemUnread(items[i])) {
        return i;
      }
    }
    return -1;
  }, [items, isItemUnread]);

  // Function to scroll to first unread item
  const scrollToFirstUnread = useCallback(() => {
    if (!parentRef.current) return;

    const firstUnreadIndex = findFirstUnreadIndex();
    if (firstUnreadIndex === -1) return;

    const scrollTop = firstUnreadIndex * itemHeight;
    parentRef.current.scrollTo({
      top: scrollTop,
      behavior: "smooth",
    });
  }, [findFirstUnreadIndex, itemHeight]);

  // Function to mark unread items as read
  const markUnreadItemsAsRead = useCallback(() => {
    if (!isFocused.current || !parentRef.current) return;

    const currentScrollTop = parentRef.current.scrollTop;
    if (currentScrollTop > 0) return; // Only mark as read if we're at the top

    // Check if there are any unread items
    const hasUnreadItems = items.some((item) => isItemUnread(item));
    if (!hasUnreadItems) return;

    // Get the latest item's id
    const latestItem = items[0];
    if (latestItem?.id) {
      // Immediately mark all items as read in the UI
      onRead(latestItem.id, { networkId: latestItem.network?.id });
      // Debounce the mutation call for the latest item
      debouncedOnRead(latestItem.id, { networkId: latestItem.network?.id });
    }
  }, [items, onRead, debouncedOnRead, isItemUnread]);

  // Handle initial load and scroll to first unread
  useEffect(() => {
    if (!initialLoadDone.current && items.length > 0 && !isLoading) {
      initialLoadDone.current = true;

      // If we have unread items and we're at the top, we might need to load more
      if (hasPrevious && findFirstUnreadIndex() === -1) {
        setIsLoadingPrevious(true);
        loadPrevious().finally(() => {
          setIsLoadingPrevious(false);
          // After loading previous items, try to scroll to first unread
          setTimeout(scrollToFirstUnread, 100);
        });
      } else {
        // If we have unread items in the current set, scroll to them
        scrollToFirstUnread();
      }
    }
  }, [
    items,
    isLoading,
    hasPrevious,
    findFirstUnreadIndex,
    loadPrevious,
    scrollToFirstUnread,
  ]);

  // Track new items above current view
  useEffect(() => {
    if (items.length > 0 && parentRef.current) {
      const firstVisibleIndex =
        Math.floor(parentRef.current.scrollTop / itemHeight) || 0;
      const newItemsAbove = items.filter(
        (item, index) => index < firstVisibleIndex && isItemUnread(item)
      ).length;

      setNewItemsCount(newItemsAbove);
      // Show banner if there are unread items above
      setShowNewItems(newItemsAbove > 0);
    }
  }, [items, itemHeight, parentRef.current?.scrollTop, isItemUnread]);

  // Track scroll position for scroll-to-top button
  const handleScroll = useCallback(
    debounce((e) => {
      if (!parentRef.current) return;

      const scrollTop = e.target.scrollTop;
      const isScrollingUp = scrollTop < lastScrollTop.current;
      lastScrollTop.current = scrollTop;

      // Show/hide scroll to top button
      setShowScrollTop(scrollTop > 100);

      // Get visible items
      const visibleRange = rowVirtualizer.getVirtualItems();
      const viewportHeight = parentRef.current.clientHeight;

      // Only consider items that are actually visible in the viewport
      const newVisibleItems = new Set(
        visibleRange
          .filter((item) => {
            const itemTop = item.start;
            const itemBottom = item.start + itemHeight;
            return (
              itemTop >= scrollTop && itemBottom <= scrollTop + viewportHeight
            );
          })
          .map((item) => item.index)
      );
      visibleItems.current = newVisibleItems;

      // Handle unread notifications when scrolling up
      if (isScrollingUp && onRead && isItemUnread) {
        // Find the oldest unread notification that is now visible
        let oldestUnreadIndex = -1;
        for (let i = 0; i < items.length; i++) {
          if (isItemUnread(items[i]) && newVisibleItems.has(i)) {
            oldestUnreadIndex = i;
            break;
          }
        }

        // If we found a new unread notification that wasn't previously handled
        if (
          oldestUnreadIndex !== -1 &&
          oldestUnreadIndex !== lastUnreadIndex.current
        ) {
          lastUnreadIndex.current = oldestUnreadIndex;
          const item = items[oldestUnreadIndex];
          onRead(item.id, { networkId: item.network?.id });
        }
      }

      // Update new items count and banner visibility
      const firstVisibleIndex = Math.floor(scrollTop / itemHeight) || 0;
      const newItemsAbove = items.filter(
        (item, index) => index < firstVisibleIndex && isItemUnread(item)
      ).length;

      setNewItemsCount(newItemsAbove);
      setShowNewItems(newItemsAbove > 0);

      // Load more when reaching the bottom
      if (
        hasMore &&
        !isLoading &&
        scrollTop + e.target.clientHeight >= e.target.scrollHeight - 100
      ) {
        console.log("Loading more items...", {
          scrollTop,
          clientHeight: e.target.clientHeight,
          scrollHeight: e.target.scrollHeight,
          hasMore,
          isLoading,
        });
        loadMore();
      }

      // Load previous items when reaching the top
      if (hasPrevious && !isLoadingPrevious && scrollTop < 100) {
        setIsLoadingPrevious(true);
        loadPrevious().finally(() => {
          setIsLoadingPrevious(false);
        });
      }
    }, 100),
    [
      items,
      loadMore,
      loadPrevious,
      hasMore,
      hasPrevious,
      isLoading,
      isLoadingPrevious,
      onRead,
      isItemUnread,
      itemHeight,
    ]
  );

  // Track new items when they're added
  useEffect(() => {
    console.log("Items changed:", {
      currentFirst: items[0]?.id,
      lastFirst: lastFirstItem,
      currentLength: items.length,
      previousLength: actualItemsLength,
      scrollTop: parentRef.current?.scrollTop,
    });

    // Check if this is a complete list replacement
    const isListReplacement =
      items.length <= actualItemsLength &&
      items[0]?.id !== lastFirstItem &&
      !items.some((item) => item.id === lastFirstItem);

    // Prevent duplicate updates within 100ms
    const now = Date.now();
    if (now - lastUpdateTime.current < 100 && !isListReplacement) {
      return;
    }

    if (
      (items[0]?.id !== lastFirstItem || isListReplacement) &&
      parentRef.current &&
      !isUpdatingScroll
    ) {
      // Items were prepended
      const newItems = [];
      let i = 0;

      if (isListReplacement) {
        // For list replacement, find items that are newer than our current view
        const lastItems = new Set(lastItemsRef.current.map((item) => item.id));
        const currentFirstItem = items[0]?.id;

        // Find the first item that exists in both lists
        let commonItemIndex = items.findIndex((item) => lastItems.has(item.id));
        if (commonItemIndex === -1) {
          // If no common items, all items are new
          newItems.push(...items);
        } else {
          // Add items before the common item
          for (i = 0; i < commonItemIndex; i++) {
            newItems.push(items[i]);
          }
        }
      } else {
        // Normal prepend case
        const lastFirstItemIndex = items.findIndex(
          (item) => item.id === lastFirstItem
        );
        if (lastFirstItemIndex === -1) {
          // If we're not at the top, only treat items before the previous length as new
          for (i = 0; i < Math.min(items.length, actualItemsLength); i++) {
            newItems.push(items[i]);
          }
        } else {
          // Normal case: find all items before lastFirstItem
          while (i < lastFirstItemIndex) {
            newItems.push(items[i]);
            i++;
          }
        }
      }

      const diff = newItems.length;
      if (diff > 0) {
        setIsUpdatingScroll(true);
        lastUpdateTime.current = now;

        // Calculate new scroll position
        const currentScrollTop = parentRef.current.scrollTop;
        const newScrollTop = currentScrollTop + diff * itemHeight;

        console.log("Updating scroll for prepend:", {
          diff,
          currentScroll: currentScrollTop,
          newScroll: newScrollTop,
          newItems: newItems.map((n) => n.id),
          isListReplacement,
          isAtTop: currentScrollTop === 0,
          currentFirstItem: items[0]?.id,
          lastFirstItem,
        });

        // Update scroll position only if we're not at the top
        if (currentScrollTop > 0 && !isListReplacement) {
          parentRef.current.scrollTop = newScrollTop;
        }

        // Always call onPrepend with the new items
        onPrepend(newItems);

        // Mark unread items as read if we're focused and at the top
        if (isFocused.current && parentRef.current.scrollTop === 0) {
          markUnreadItemsAsRead();
        }

        // Reset updating flag after a short delay
        setTimeout(() => {
          setIsUpdatingScroll(false);
        }, 50);
      }
    } else if (items.length > actualItemsLength) {
      const newItemIndices = new Set();
      for (let i = actualItemsLength; i < items.length; i++) {
        newItemIndices.add(i);
      }
      setNewItems(newItemIndices);

      // Call onAppend with the new items
      const newItems = items.slice(actualItemsLength);
      onAppend(newItems);

      // Clear the new items after animation
      setTimeout(() => {
        setNewItems(new Set());
      }, 300);
    } else if (items.length < actualItemsLength && parentRef.current) {
      // Items were removed
      const removedItemIndices = new Set();
      for (let i = items.length; i < actualItemsLength; i++) {
        removedItemIndices.add(i);
      }
      setNewItems(removedItemIndices);

      // Clear the new items after animation
      setTimeout(() => {
        setNewItems(new Set());
      }, 300);
    }

    setLastFirstItem(items[0]?.id);
    setActualItemsLength(items.length);
    lastItemsRef.current = items;
  }, [
    items,
    actualItemsLength,
    itemHeight,
    onAppend,
    onPrepend,
    isItemUnread,
    debouncedOnRead,
    markUnreadItemsAsRead,
  ]);

  // Clean up debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedOnRead.cancel();
      if (loadMoreTimeout.current) {
        clearTimeout(loadMoreTimeout.current);
      }
    };
  }, [debouncedOnRead]);

  // Initialize refs when component mounts
  useEffect(() => {
    if (parentRef.current) {
      lastScrollTop.current = parentRef.current.scrollTop;
    }
  }, []);

  // Update loading state
  useEffect(() => {
    if (isLoading) {
      setLoadingHeight(100);
    } else {
      setLoadingHeight(0);
    }
  }, [isLoading]);

  // Reset lastUnreadIndex when items change
  useEffect(() => {
    lastUnreadIndex.current = -1;
  }, [items]);

  const scrollToTop = useCallback(() => {
    if (parentRef.current) {
      parentRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, []);

  const virtualizerConfig = useMemo(
    () => ({
      count: items.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => itemHeight,
      overscan,
    }),
    [items.length, itemHeight, overscan]
  );

  const rowVirtualizer = useVirtualizer(virtualizerConfig);

  // Generate skeleton items for loading state
  const skeletonItems = useMemo(() => {
    if (!SkeletonComponent) return [];
    const count = Math.ceil(parentRef.current?.clientHeight / itemHeight) || 25;
    return Array(count).fill(null);
  }, [itemHeight, SkeletonComponent]);

  // Handle empty state visibility with a delay
  useEffect(() => {
    if (isLoading) {
      setShowEmptyState(false);
    } else if (items.length === 0) {
      const timer = setTimeout(() => {
        setShowEmptyState(true);
      }, 500); // 500ms delay before showing empty state
      return () => clearTimeout(timer);
    } else {
      setShowEmptyState(false);
    }
  }, [isLoading, items.length]);

  return (
    <Box
      ref={parentRef}
      className={className}
      style={{
        height: containerHeight,
        overflow: "auto",
        position: "relative",
        zIndex: 1,
        ...style,
      }}
      onScroll={handleScroll}
    >
      {/* Loading indicator for previous items */}
      {isLoadingPrevious && SkeletonComponent && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: itemHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: theme.palette.background.paper,
            zIndex: 2,
            pb: gap,
          }}
        >
          <SkeletonComponent />
        </Box>
      )}

      {/* New Items Banner */}
      <Fade in={showNewItems && newItemsCount > 0}>
        <Paper
          elevation={2}
          onClick={scrollToTop}
          sx={{
            position: "sticky",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            p: 1.5,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderRadius: 1,
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            marginBottom: "-40px",
            "&:hover": {
              bgcolor: theme.palette.primary.dark,
              transform: "translateY(-1px)",
              boxShadow: theme.shadows[4],
            },
          }}
        >
          <Typography
            variant="body2"
            align="center"
            sx={{
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <KeyboardArrowUpIcon fontSize="small" />
            {newItemsCount} {newItemsText}
          </Typography>
        </Paper>
      </Fade>

      <Box
        style={{
          height: `${rowVirtualizer.getTotalSize() + (isLoading && items.length > 0 ? 40 : 0)}px`,
          width: "100%",
          position: "relative",
          zIndex: 0,
        }}
      >
        {isLoading && items.length === 0 && SkeletonComponent ? (
          // Show skeleton items when loading initial data
          skeletonItems.map((_, index) => (
            <Box
              key={`skeleton-${index}`}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: itemHeight,
                transform: `translateY(${index * (itemHeight + gap)}px)`,
                willChange: "transform",
                zIndex: 0,
              }}
            >
              <SkeletonComponent />
            </Box>
          ))
        ) : showEmptyState && emptyState ? (
          // Show empty state with fade transition
          <Fade in={showEmptyState}>
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pt: { xs: 6, sm: 8 },
                px: { xs: 2, sm: 4, md: 6, lg: 20 }, // Match header margins
                pointerEvents: "none",
              }}
            >
              <Paper
                sx={{
                  p: { xs: 3, sm: 4 },
                  textAlign: "center",
                  width: "100%",
                  maxWidth: { xs: "100%", sm: 400 },
                  pointerEvents: "auto",
                  bgcolor: "background.paper",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
                elevation={2}
              >
                {emptyState.icon && (
                  <Box
                    sx={{
                      fontSize: { xs: 40, sm: 48 },
                      color: "text.secondary",
                      mb: { xs: 1.5, sm: 2 },
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {emptyState.icon}
                  </Box>
                )}
                <Typography
                  variant="h6"
                  color="text.secondary"
                  gutterBottom
                  sx={{
                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                  }}
                >
                  {emptyState.title}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  }}
                >
                  {emptyState.description}
                </Typography>
              </Paper>
            </Box>
          </Fade>
        ) : (
          <>
            {/* Show actual items */}
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const item = items[virtualRow.index];
              const isNew =
                virtualRow.index === 0 && newItems.has(virtualRow.index);

              return (
                <Box
                  key={item.id}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: itemHeight,
                    transform: `translateY(${virtualRow.start}px)`,
                    willChange: "transform",
                    zIndex: 0,
                    paddingBottom: gap,
                  }}
                >
                  <Grow in={true} timeout={300}>
                    {renderItem(item)}
                  </Grow>
                </Box>
              );
            })}

            {/* Loading indicator at bottom */}
            {isLoading && items.length > 0 && (
              <Box
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: 40,
                  transform: `translateY(${rowVirtualizer.getTotalSize()}px)`,
                  willChange: "transform",
                  zIndex: 0,
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: theme.palette.primary.main,
                      animation: "pulse 1s infinite",
                      "@keyframes pulse": {
                        "0%": {
                          transform: "scale(0.8)",
                          opacity: 0.5,
                        },
                        "50%": {
                          transform: "scale(1.2)",
                          opacity: 1,
                        },
                        "100%": {
                          transform: "scale(0.8)",
                          opacity: 0.5,
                        },
                      },
                    }}
                  />
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: theme.palette.primary.main,
                      animation: "pulse 1s infinite 0.2s",
                      "@keyframes pulse": {
                        "0%": {
                          transform: "scale(0.8)",
                          opacity: 0.5,
                        },
                        "50%": {
                          transform: "scale(1.2)",
                          opacity: 1,
                        },
                        "100%": {
                          transform: "scale(0.8)",
                          opacity: 0.5,
                        },
                      },
                    }}
                  />
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: theme.palette.primary.main,
                      animation: "pulse 1s infinite 0.4s",
                      "@keyframes pulse": {
                        "0%": {
                          transform: "scale(0.8)",
                          opacity: 0.5,
                        },
                        "50%": {
                          transform: "scale(1.2)",
                          opacity: 1,
                        },
                        "100%": {
                          transform: "scale(0.8)",
                          opacity: 0.5,
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Scroll to Top Button */}
      <Fade in={showScrollTop}>
        <IconButton
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            right: 24,
            bottom: 24,
            zIndex: 10,
            bgcolor: theme.palette.background.paper,
            color: theme.palette.primary.main,
            boxShadow: theme.shadows[4],
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              transform: "translateY(-2px)",
              boxShadow: theme.shadows[6],
            },
          }}
        >
          <KeyboardArrowUpIcon />
        </IconButton>
      </Fade>
    </Box>
  );
};

export default VirtualList;
